import React, { useEffect, useRef, useState, useCallback } from 'react';

import { Col, Switch, Select, Tooltip, Radio, Alert, DatePicker as AntDatePicker } from 'antd';

import CustomMaskedInput from 'components/MaskedInput';
import { constants } from 'helpers/constants';
import { functions } from 'helpers/functions';
import { EChargeType } from 'interfaces/IEditProduct';
import type IProduct from 'interfaces/IProduct';
import { OfferReadjustmentAlert } from 'v2/components/Shared/OfferReadjustmentAlert';
import { getCurrencySymbol } from 'v2/helpers/getCurrencySymbol';
import { useCurrencies } from 'v2/hooks/useCurrencies';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { useUserStore } from 'v2/store/User';
import { run } from 'validations/ruleRunner';

import SendBillingReminderSetting from './SendBillingReminderSetting';
import { allUsersSubscriptionValidation } from './validations';
import PermanentAccessAlertModal from '../../../../../NewProduct/components/Forms/Sales/components/Subscription/PermanentAccessAlertModal/index';
import BlockedFieldTooltipProps from '../../../General/blockedTooltipProps';

export const recurrencyFrequencyType = {
  Mensal: { abbr: 'M', freq: 1 },
  Trimestral: { abbr: 'TM', freq: 3 },
  Semestral: { abbr: 'SM', freq: 1 },
  Anual: { abbr: 'Y', freq: 1 },
  Personalizado: { abbr: 'P', freq: 2 }
};
const CUSTOM_RECURRENCY = 'P';
const OPEN_PRICE_BLOCK_MESSAGE = 'Indisponível quando Valor Aberto está ativo.';

interface IProps {
  product: IProduct;
  handleChange: any;
  modifieds: any;
  editing: any;
  onChangeRequiredFields: any;
  loading: boolean;
  isAlumy?: boolean;
}

function Subscription(props: IProps) {
  const { product, handleChange, onChangeRequiredFields, loading, isAlumy } = props;
  const { user } = useUserStore(({ user }) => ({ user }));

  const { data: currencies } = useCurrencies();

  const prevProductRef = useRef<IProduct | undefined>(undefined);

  const [_showErrors, _setShowErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState(() => run(props.product, allUsersSubscriptionValidation));
  const [showStartChargingAt, setShowStartChargingAt] = useState(
    () => props.product.recurring_start_charging !== '' || props.product.recurring_days_start_charging != null
  );
  const [chargeAtType, setChargeAtType] = useState(() =>
    props.product.recurring_days_start_charging !== null ? 'days' : 'date'
  );
  const [showOfferReadjustmentAlert, setShowOfferReadjustmentAlert] = useState(false);
  const [showPermanentAccessAlertModal, setShowPermanentAccessAlertModal] = useState(false);

  const errorFor = (field: any) => {
    return validationErrors[field] || '';
  };

  const isValid = (field: any) => {
    return validationErrors[field] ? true : false;
  };

  const changeStates = useCallback(() => {
    const newValidationErrors = run(product, allUsersSubscriptionValidation);
    setValidationErrors(newValidationErrors);

    if (onChangeRequiredFields) {
      onChangeRequiredFields('subscriptionErrors', newValidationErrors);
    }
  }, [product, onChangeRequiredFields]);

  const checkIfIsCustomRecurring = useCallback(() => {
    const monthsTypes = ['M', 'TM', 'SM'];

    if (monthsTypes.includes(product.recurring_frequency_type) && product.recurring_frequency > 1) {
      handleChange('recurring_frequency_type', CUSTOM_RECURRENCY);
      handleChange('recurring_frequency_type_select', 'MONTH');
    }

    if (product.recurring_frequency_type === 'Y' && product.recurring_frequency > 1) {
      handleChange('recurring_frequency_type', CUSTOM_RECURRENCY);
      handleChange('recurring_frequency_type_select', 'YEAR');
    }
  }, [product, handleChange]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const isSubscription = product.charge_type === EChargeType.SUBSCRIPTION;
    const prevProduct = prevProductRef.current;
    const hasChangedProduct = prevProduct !== undefined && product !== prevProduct;

    if (isSubscription && (prevProduct === undefined || hasChangedProduct)) {
      changeStates();
    }

    checkIfIsCustomRecurring();
    prevProductRef.current = product;
  }, [product, loading, checkIfIsCustomRecurring, changeStates]);

  const showOfferReadjustmentAlertHandler = () => {
    setShowOfferReadjustmentAlert(true);
  };

  const startChargingClear = (value: boolean) => {
    const newShowStartChargingAt = value;

    if (!newShowStartChargingAt) {
      handleChange('recurring_start_charging', '', () => handleChange('recurring_days_start_charging', ''));
    }

    setShowStartChargingAt(newShowStartChargingAt);
  };

  const handleChangeAndClearStartDateDays = (value: string) => {
    handleChange('recurring_start_charging', '', () => handleChange('recurring_days_start_charging', ''));
    setChargeAtType(value);
  };

  const handleChangeRecurrencyType = (value: string) => {
    const isChangingFromMonthly = product.recurring_frequency_type === 'M' && value !== 'M';
    const isChangingToMonthly = value === 'M';
    handleChange('recurring_frequency_type', value);

    if (isChangingFromMonthly) {
      handleChange('send_billing_reminder', true);
    }

    if (isChangingToMonthly) {
      handleChange('send_billing_reminder', false);
    }

    if (value === CUSTOM_RECURRENCY) {
      handleChange('recurring_frequency', 2);
      handleChange('recurring_frequency_type_select', 'MONTH');
      return;
    }

    handleChange('recurring_frequency', 1);
  };

  const handleChangeRecurrencyFrequency = (value: number) => {
    const isCustomRecurrenceType = product.recurring_frequency_type === CUSTOM_RECURRENCY;
    handleChange('send_billing_reminder', isCustomRecurrenceType && value !== 1);
    handleChange('recurring_frequency', value);
  };

  const handleChangeAfterRadio = () => {
    handleChange('undetermined_charges', false);
    handleChange('limit_charges', product.limit_charges);
  };

  const onChangeTrial = (value: boolean) => {
    if (value) {
      handleChange('allows_boleto', false);
    }

    handleChange('trial', value);
  };

  const getEnableInstallmentTitle = (productParam: IProduct) => {
    const { trial, membership_fee, type } = productParam;

    if (trial) {
      return 'O Parcelamento da Recorrência não pode ser habilitado junto com o Trial';
    }

    if (membership_fee) {
      return 'O Parcelamento da Recorrência não pode ser habilitado junto com a Adesão';
    }

    if (type === constants.PHYSICAL) {
      return 'O Parcelamento da Recorrência não pode ser habilitado em produtos físicos';
    }

    return '';
  };

  const handleChangePermanentAccess = (permanentAccess: boolean) => {
    if (permanentAccess) {
      setShowPermanentAccessAlertModal(true);
    }

    handleChange('permanent_access', permanentAccess);
  };

  const handleClosePermanentAccessModal = () => {
    setShowPermanentAccessAlertModal(false);
  };

  const isMonthly = () => {
    const isMonthlyType = product.recurring_frequency_type === 'M';
    const isCustomMonthly = product.recurring_frequency_type === CUSTOM_RECURRENCY && product.recurring_frequency === 1;
    return isMonthlyType || isCustomMonthly;
  };

  const userIsEadCharging = useUserStore(({ user }) => user?.eadCharging || false);

  const currencyCode = currencies.find(currency => currency.id === product.currency_id)?.code;
  const currencySymbol = currencyCode ? getCurrencySymbol('pt-BR', currencyCode) : '';
  const isBetaReadjustmentAlert = eduzzAccountsService.verifyUserHasBetaModule('assinaturas-reajuste');
  const isUnity = user?.is_clube_black;
  const isElite = user?.is_elite;
  const isMonthlyValue = isMonthly();
  const notMonthlyCustomFrequency = product.recurring_frequency_type_select !== 'MONTH';

  return (
    <>
      <Col xs={24} sm={12} className='sm:max-w-[350px] sm:flex-1'>
        <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
          <div>
            <CustomMaskedInput
              label={product.open_price ? 'Valor mínimo' : 'Preço'}
              helperText={isValid('recurring_value') ? errorFor('recurring_value') : 'Ex: 1.000,00'}
              error={isValid('recurring_value')}
              fullWidth={true}
              disableUnderline={true}
              id='recurring_value'
              mask='money'
              startAdornment={currencySymbol}
              value={functions.addMoneyMask(product.recurring_value)}
              onChange={(value: any) => handleChange('recurring_value', value)}
              InputLabelProps={{
                shrink: true
              }}
              disabled={isAlumy}
              onFocus={() => {
                if (!isUnity && !isBetaReadjustmentAlert && !isElite) {
                  return;
                }

                showOfferReadjustmentAlertHandler();
              }}
            />
          </div>
        </BlockedFieldTooltipProps>
      </Col>
      <Col span={24}>
        <OfferReadjustmentAlert show={showOfferReadjustmentAlert} />
      </Col>
      <Col span={24}>
        <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
          <div>
            <label className='mb-1 block text-sm'>Frequência de cobrança</label>
            <Select
              id='recurring_frequency_type'
              className='w-full'
              value={product.recurring_frequency_type}
              onChange={handleChangeRecurrencyType}
              disabled={isAlumy}
              options={(Object.keys(recurrencyFrequencyType) as Array<keyof typeof recurrencyFrequencyType>).map(
                type => ({
                  value: recurrencyFrequencyType[type].abbr,
                  label: type
                })
              )}
            />
          </div>
        </BlockedFieldTooltipProps>
      </Col>
      {product.recurring_frequency_type === CUSTOM_RECURRENCY && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <p>Repetir a cada:</p>
                <div style={{ width: '50px', margin: '0 10px', display: 'flex' }}>
                  <CustomMaskedInput
                    id='recurring_frequency'
                    error={isValid('recurring_frequency')}
                    mask='number'
                    disableUnderline={true}
                    value={product.recurring_frequency}
                    onChange={(value: any) => handleChangeRecurrencyFrequency(Number(value))}
                    disabled={isAlumy}
                  />
                </div>
                <Select
                  onChange={value => handleChange('recurring_frequency_type_select', value)}
                  value={product.recurring_frequency_type_select}
                  options={[
                    { value: 'MONTH', label: product.recurring_frequency > 1 ? 'meses' : 'mês' },
                    { value: 'YEAR', label: product.recurring_frequency > 1 ? 'anos' : 'ano' }
                  ]}
                />
              </div>
              {isValid('recurring_frequency') && (
                <p className='mt-1 text-xs text-red-500'>{errorFor('recurring_frequency')}</p>
              )}
            </>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      <Col span={24}>
        <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
          <div>
            <div className={isAlumy ? 'pointer-events-none text-black/[0.38]' : ''}>A cobrança termina em:</div>
            <div>
              <Radio.Group value={product.undetermined_charges ? 'never' : 'after'}>
                <Radio
                  value='never'
                  onChange={() => {
                    handleChange('limit_charges', '2');
                    handleChange('undetermined_charges', true);
                  }}
                  disabled={isAlumy}
                >
                  Nunca
                </Radio>
              </Radio.Group>
              <div>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Radio.Group value={product.undetermined_charges ? 'never' : 'after'}>
                    <Radio value='after' onChange={handleChangeAfterRadio} disabled={isAlumy}>
                      Após
                    </Radio>
                  </Radio.Group>
                  <div style={{ width: 57 }}>
                    <CustomMaskedInput
                      id='limit_charges'
                      mask='number'
                      error={isValid('limit_charges')}
                      onChange={(value: string) => {
                        if (value.length > 2) {
                          return;
                        }

                        handleChange('limit_charges', value);
                      }}
                      value={product.limit_charges}
                      disabled={isAlumy || product.undetermined_charges}
                    />
                  </div>
                  <span className={isAlumy ? 'pointer-events-none text-black/[0.38]' : ''}>
                    {product.limit_charges > 1 ? 'cobranças' : 'cobrança'}
                  </span>
                </div>
                {isValid('limit_charges') && <p className='mt-1 text-xs text-red-500'>{errorFor('limit_charges')}</p>}
              </div>
            </div>
          </div>
        </BlockedFieldTooltipProps>
      </Col>
      <Col span={24}>
        <Tooltip
          title={
            product.open_price
              ? OPEN_PRICE_BLOCK_MESSAGE
              : product.trial
                ? 'A Adesão não pode ser habilitada junto com o Trial'
                : product.yearly_installment
                  ? 'A Adesão não pode ser habilitado junto com o Parcelamento da Recorrência'
                  : product.type === constants.PHYSICAL
                    ? 'A Adesão não pode ser habilitado em produtos físicos'
                    : ''
          }
          placement='bottom'
        >
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <div className='flex items-center gap-2'>
                <Switch
                  checked={product.membership_fee}
                  onChange={checked => handleChange('membership_fee', checked)}
                  disabled={
                    product.open_price ||
                    product.trial ||
                    product.yearly_installment ||
                    product.type === constants.PHYSICAL ||
                    isAlumy
                  }
                />
                <span>Taxa de contratação / adesão</span>
              </div>
              <p className='mt-1 text-xs text-gray-500'>
                {product.open_price ? OPEN_PRICE_BLOCK_MESSAGE : 'O cliente deve pagar taxa de contratação'}
              </p>
            </div>
          </BlockedFieldTooltipProps>
        </Tooltip>
      </Col>
      {product.membership_fee && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <CustomMaskedInput
                label='Valor da adesão'
                id='membership_price'
                mask='money'
                error={isValid('membership_price')}
                value={functions.addMoneyMask(product.membership_price)}
                onChange={(value: any) => handleChange('membership_price', value)}
                fullWidth={true}
                disableUnderline={true}
                disabled={!product.membership_fee || isAlumy}
              />
              <p className='mt-1 text-xs text-gray-500'>
                {isValid('membership_price') ? errorFor('membership_price') : 'Ex: R$ 20,00'}
              </p>
            </div>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      {product.membership_fee && userIsEadCharging && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <div className='flex items-center gap-2'>
                <Switch
                  checked={showStartChargingAt}
                  onChange={startChargingClear}
                  disabled={!product.membership_fee || isAlumy}
                />
                <span>Alterar data de inicio de cobrança</span>
              </div>
            </div>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      {product.membership_fee && showStartChargingAt && userIsEadCharging && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <label className='mb-1 block text-sm'>Data / Número de Dias</label>
              <Select
                className='w-full'
                value={chargeAtType}
                onChange={handleChangeAndClearStartDateDays}
                disabled={!showStartChargingAt || isAlumy}
                options={[
                  { value: 'date', label: 'Data' },
                  { value: 'days', label: 'Número de Dias' }
                ]}
              />
              <p className='mt-1 text-xs text-gray-500'>Início de cobrança por data ou dias</p>
            </div>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      {product.membership_fee && userIsEadCharging && showStartChargingAt && chargeAtType !== 'days' && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <label className='mb-1 block text-sm'>Começar a cobrar após</label>
              <AntDatePicker
                className='w-full'
                format='DD/MM/YYYY'
                placeholder='DD/MM/YYYY'
                value={product.recurring_start_charging ? product.recurring_start_charging : undefined}
                onChange={(date: any) => handleChange('recurring_start_charging', date)}
                disabled={(!showStartChargingAt && chargeAtType === 'date') || isAlumy}
              />
              <p className='mt-1 text-xs text-gray-500'>A cobrança iniciará após da data escolhida</p>
            </div>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      {userIsEadCharging && showStartChargingAt && chargeAtType === 'days' && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <CustomMaskedInput
                label='Começar a cobrar após'
                fullWidth={true}
                disableUnderline={true}
                id='recurring_days_start_charging'
                mask='number'
                error={isValid('recurring_days_start_charging')}
                helperText={
                  isValid('recurring_days_start_charging')
                    ? 'Informe um valor entre 1 e 365 dias.'
                    : 'A cobrança iniciará após o número de dias escolhidos em relação a data de faturamento'
                }
                value={product.recurring_days_start_charging}
                onChange={(value: any) => handleChange('recurring_days_start_charging', value)}
                InputLabelProps={{
                  shrink: true
                }}
                disabled={isAlumy}
              />
            </div>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      <Col span={24}>
        <Tooltip
          title={
            product.open_price
              ? OPEN_PRICE_BLOCK_MESSAGE
              : product.membership_fee
                ? 'O Trial não pode ser habilitado junto com a Adesão'
                : product.yearly_installment
                  ? 'O Trial não pode ser habilitado junto com o Parcelamento da Recorrência'
                  : product.type === constants.PHYSICAL
                    ? 'O Trial não pode ser habilitado em produtos físicos'
                    : ''
          }
          placement='bottom'
        >
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <div className='flex items-center gap-2'>
                <Switch
                  checked={product.trial}
                  onChange={onChangeTrial}
                  disabled={
                    product.open_price || product.membership_fee || product.type === constants.PHYSICAL || isAlumy
                  }
                />
                <span>Período trial</span>
              </div>
              <p className='mt-1 text-xs text-gray-500'>
                {product.open_price ? OPEN_PRICE_BLOCK_MESSAGE : 'Permitir que o cliente experimente o produto'}
              </p>
            </div>
          </BlockedFieldTooltipProps>
        </Tooltip>
      </Col>
      {product.trial && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <CustomMaskedInput
                label='Dias para trial'
                id='trial_days'
                mask='number'
                error={isValid('trial_days')}
                value={product.trial_days}
                onChange={(value: any) => handleChange('trial_days', value)}
                fullWidth={true}
                disableUnderline={true}
                disabled={!product.trial || isAlumy}
              />
              <p className='mt-1 text-xs text-gray-500'>
                {isValid('trial_days') ? errorFor('trial_days') : 'Ex: 15 dias'}
              </p>
            </div>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      {!product.undetermined_charges && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <div className='flex items-center gap-2'>
                <Switch checked={product.permanent_access} onChange={handleChangePermanentAccess} disabled={isAlumy} />
                <span>Acesso permanente</span>
              </div>
              <p className='mt-1 text-xs text-gray-500'>Mantém o acesso mesmo após o término das cobranças.</p>
            </div>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      {!isMonthlyValue && notMonthlyCustomFrequency && (
        <Col span={24}>
          <Tooltip title={getEnableInstallmentTitle(product)} placement='bottom'>
            <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
              <div>
                <div className='flex items-center gap-2'>
                  <Switch
                    checked={product.yearly_installment}
                    onChange={checked => handleChange('yearly_installment', checked)}
                    disabled={product.membership_fee || product.type === constants.PHYSICAL || isAlumy}
                  />
                  <span>Habilitar parcelamento no Checkout</span>
                </div>
                <p className='mt-1 text-xs text-gray-500'>
                  Permite que o cliente possa parcelar a cobrança no Checkout.
                </p>
              </div>
            </BlockedFieldTooltipProps>
          </Tooltip>
        </Col>
      )}
      {!isMonthlyValue && (
        <Col span={24}>
          <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
            <div>
              <SendBillingReminderSetting
                isActive={product.send_billing_reminder}
                onChange={isActive => handleChange('send_billing_reminder', isActive)}
                disabled={isAlumy}
              />
            </div>
          </BlockedFieldTooltipProps>
        </Col>
      )}
      {product.isVariation() && product.undetermined_charges && (
        <Col span={24}>
          <div className='flex items-center gap-2'>
            <Switch
              onChange={checked => handleChange('allows_upgrade', checked)}
              checked={product.allows_upgrade}
              disabled={isAlumy || product.open_price}
            />
            <span>Habilitar upgrade de plano</span>
          </div>
          <p className='mt-1 text-xs text-gray-500'>
            {product.open_price
              ? OPEN_PRICE_BLOCK_MESSAGE
              : 'Permite que o cliente possa subir de plano de assinatura entre as outras variações desse produto que também tenha essa opção habilitada.'}
          </p>
        </Col>
      )}
      {product.isVariation() && product.undetermined_charges && (
        <Col span={24}>
          <div className='flex items-center gap-2'>
            <Switch
              checked={product.allows_downgrade}
              onChange={checked => handleChange('allows_downgrade', checked)}
              disabled={isAlumy || product.open_price}
            />
            <span>Habilitar downgrade de plano</span>
          </div>
          <p className='mt-1 text-xs text-gray-500'>
            {product.open_price
              ? OPEN_PRICE_BLOCK_MESSAGE
              : 'Permite que o cliente possa descer de plano de assinatura entre as outras variações desse produto que também tenha essa opção habilitada.'}
          </p>
        </Col>
      )}
      <Col span={24}>
        <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
          <div>
            <div className='flex items-center gap-2'>
              <Switch
                checked={product.remove_access}
                onChange={checked => handleChange('remove_access', checked)}
                disabled={isAlumy}
              />
              <span>Remover acesso ao conteúdo</span>
            </div>
            <p className='mt-1 text-xs text-gray-500'>
              O acesso ao conteúdo será removido caso a assinatura fique em atraso.
            </p>
          </div>
        </BlockedFieldTooltipProps>
      </Col>
      <Col span={24}>
        <BlockedFieldTooltipProps partnerCod={product.cnt_partner_cod} enabled={isAlumy}>
          <div>
            <CustomMaskedInput
              label='Limite de atraso'
              id='overdue_limit'
              mask='number'
              error={isValid('overdue_limit')}
              fullWidth={true}
              disableUnderline={true}
              defaultValue={5}
              value={product.overdue_limit}
              onChange={(value: any) => handleChange('overdue_limit', value)}
              disabled={isAlumy}
            />
            <p className='mt-1 text-xs text-gray-500'>
              {isValid('overdue_limit')
                ? errorFor('overdue_limit')
                : `Após ${product.overdue_limit} cobrança(s) consecutivas em atraso, a assinatura será cancelada automaticamente.`}
            </p>
            <Alert
              type='warning'
              style={{ width: '100%', marginTop: 12 }}
              message="A alteração do 'Limite de atraso' afetará apenas os contratos criados após a modificação. Para os contratos antigos a configuração não será alterada."
            />
          </div>
        </BlockedFieldTooltipProps>
      </Col>
      <PermanentAccessAlertModal isOpen={showPermanentAccessAlertModal} handleClose={handleClosePermanentAccessModal} />
    </>
  );
}

export default Subscription;
